import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  Unsubscribe,
  confirmPasswordReset,
  verifyPasswordResetCode,
  Auth,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, Firestore } from 'firebase/firestore';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { FirebaseService } from '../firebase.service';
import { ClientContextService } from '../client-context.service';
import { User } from '../../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  user = signal<User | null>(null);

  private firebaseService = inject(FirebaseService);
  private clientContextService = inject(ClientContextService);
  private auth: Auth = this.firebaseService.auth;
  private db: Firestore = this.firebaseService.db;
  private unsubscribe: Unsubscribe | null = null;

  constructor() {
    // Set up auth state listener
    this.unsubscribe = onAuthStateChanged(
      this.auth,
      async (firebaseUser) => {
        if (firebaseUser) {
          // User is signed in, get additional user data
          const userData = await this.getUserInfo(firebaseUser.uid);
          if (userData) {
            this.user.set(userData);
          } else {
            // Fallback to basic Firebase user data
            this.user.set({
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || '',
              photoUrl: firebaseUser.photoURL || '',
              role: 'front_desk' // Default role for users without Firestore document
            });
          }
        } else {
          // User is signed out
          this.user.set(null);
        }
      }
    );
  }

  ngOnDestroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  /**
   * Get Firebase auth state (needed by guards)
   */
  async getAuthState(): Promise<FirebaseUser | null> {
    await this.auth.authStateReady();
    return Promise.resolve(this.auth.currentUser);
  }

  /**
   * Sign in with email and password
   */
  async login(email: string, password: string): Promise<void> {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
      
      // Update login tracking and check status
      await this.updateLoginTracking();
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Sign out current user and clear client context
   */
  async logout(): Promise<void> {
    // Clear client context on logout to ensure clean state for next login
    this.clientContextService.clearOnLogout();
    await this.auth.signOut();
  }

  /**
   * Send password reset email via Cloud Function.
   * Uses the SendGrid Firebase Extension (mail collection) for branded,
   * consistent delivery — the same pipeline as inviteUser and resendInvitation.
   */
  async resetPassword(email: string): Promise<void> {
    const fn = httpsCallable(this.firebaseService.functions, 'sendPasswordResetEmail');
    await fn({ email });
  }

  /**
   * Confirm password reset with code
   */
  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    await confirmPasswordReset(this.auth, code, newPassword);
  }

  /**
   * Verify a password reset action code and return the associated email address.
   * Called before showing the set-password form to validate the link is still valid.
   */
  async verifyPasswordResetCode(code: string): Promise<string> {
    return verifyPasswordResetCode(this.auth, code);
  }

  /**
   * Get additional user information from Firestore
   */
  private async getUserInfo(uid: string): Promise<User | null> {
    try {
      const userRef = doc(this.db, 'users', uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          uid,
          email: data['email'] || '',
          name: data['displayName'] || '',
          photoUrl: data['photoUrl'] || '',
          role: data['role'] || 'front_desk',
          clientId: data['clientId'],
          propertyIds: data['propertyIds'] || []
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user info:', error);
      return null;
    }
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const currentUser = this.user();
    return currentUser?.role === role;
  }

  /**
   * Set custom claims for a user (admin only)
   */
  async setCustomClaims(email: string, claims: Record<string, unknown>): Promise<HttpsCallableResult<unknown>> {
    const setCustomClaims = httpsCallable(this.firebaseService.functions, 'setCustomClaims');
    return setCustomClaims({ email, claims });
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.user() !== null;
  }

  /**
   * Update login tracking after successful authentication
   * Checks user status and updates lastLogin timestamp
   */
  private async updateLoginTracking(): Promise<void> {
    try {
      const updateLoginTracking = httpsCallable(this.firebaseService.functions, 'updateLoginTracking');
      await updateLoginTracking({});
    } catch (error: unknown) {
      // Check if user is inactive
      if (error instanceof Error && error.message.includes('INACTIVE_USER')) {
        // Sign out the user
        await this.auth.signOut();
        throw new Error('Your account is inactive. Please contact your administrator for assistance.');
      }
      // Log other errors but don't prevent login
      console.error('Error updating login tracking:', error);
    }
  }
}
